import React, { useEffect, useState, useRef } from "react";
import styles from "./VolonterProfile.module.scss";
import PharmaciesImagesModal from "../StatusComponent/PharmacieImagesModal/PharmaciesImagesModal";
import TextField from "../TextField/TextField";
import Button from "../UI/Buttons/Button/Button";
import axios, { baseUrl } from "../../axios/axios";
import HeartIcon from "@rsuite/icons/Heart";
import NotificationMessage from "../UI/NotificationMessage/NotificationMessage";
import { useDispatch } from "react-redux";
import {
  getUserSignature,
  uploadUserSignature,
  deleteUserSignature,
} from "../../services/userSignatureService";
import { fetchDriverSignature } from "../../store/auth-actions";

type Props = {};

// =============================================================================
// IMPROVED BANK ACCOUNT VALIDATION UTILITIES
// =============================================================================

interface BankAccountValidation {
  isValid: boolean;
  errorMessage?: string;
  normalizedValue?: string;
}

/**
 * Calculates MOD 97 for large numbers using string arithmetic
 * Avoids JavaScript precision issues with 16+ digit numbers
 */
const calculateMod97 = (numberString: string): number => {
  let remainder = 0;

  for (let i = 0; i < numberString.length; i++) {
    remainder = (remainder * 10 + parseInt(numberString[i])) % 97;
  }

  return remainder;
};

/**
 * Validates Serbian bank account control number using ISO 7064 MOD 97-10
 * More robust calculation that handles large numbers correctly
 */
const validateControlNumber = (
  bankAndAccount: string,
  controlDigits: string
): boolean => {
  if (bankAndAccount.length !== 16 || controlDigits.length !== 2) {
    return false;
  }

  // Calculate: 98 - mod((bankAndAccount * 100), 97)
  const numberWith100 = bankAndAccount + "00"; // Multiply by 100 by appending "00"
  const remainder = calculateMod97(numberWith100);
  const calculatedControl = 98 - remainder;

  // Handle case where calculated control is single digit (should be padded with 0)
  const controlNumber = calculatedControl.toString().padStart(2, "0");

  return controlNumber === controlDigits;
};

/**
 * Normalizes input to 18-digit format and validates
 */
const validateAndNormalizeBankAccount = (
  input: string
): BankAccountValidation => {
  if (!input || input.trim() === "") {
    return { isValid: true, normalizedValue: "" };
  }

  // Remove all non-numeric characters
  const digitsOnly = input.replace(/\D/g, "");

  // Check if already in 18-digit format
  if (digitsOnly.length === 18) {
    const bankCode = digitsOnly.substring(0, 3);
    const accountNumber = digitsOnly.substring(3, 16);
    const controlNumber = digitsOnly.substring(16, 18);

    if (validateControlNumber(bankCode + accountNumber, controlNumber)) {
      return {
        isValid: true,
        normalizedValue: digitsOnly,
      };
    } else {
      return {
        isValid: false,
        errorMessage: "Kontrolni broj nije ispravan",
      };
    }
  }

  // Try to parse XXX-YYYYY-ZZ format
  const dashParts = input.split("-");
  if (dashParts.length === 3) {
    const [bankCode, accountNumber, controlNumber] = dashParts;

    // Validate each part
    if (!/^\d{3}$/.test(bankCode)) {
      return {
        isValid: false,
        errorMessage: "Šifra banke mora biti 3 cifre",
      };
    }

    if (!/^\d{1,13}$/.test(accountNumber)) {
      return {
        isValid: false,
        errorMessage: "Broj računa mora biti 1-13 cifara",
      };
    }

    if (!/^\d{2}$/.test(controlNumber)) {
      return {
        isValid: false,
        errorMessage: "Kontrolni broj mora biti 2 cifre",
      };
    }

    // Pad account number to 13 digits and create full number
    const paddedAccount = accountNumber.padStart(13, "0");
    const fullNumber = bankCode + paddedAccount + controlNumber;

    // Validate control number
    if (validateControlNumber(bankCode + paddedAccount, controlNumber)) {
      return {
        isValid: true,
        normalizedValue: fullNumber,
      };
    } else {
      return {
        isValid: false,
        errorMessage: "Kontrolni broj nije ispravan",
      };
    }
  }

  // Invalid format
  return {
    isValid: false,
    errorMessage: "Format: 18 cifara ili XXX-YYYYY-ZZ (npr. 200-123456-00)",
  };
};

/**
 * Formats 18-digit number for display with dashes
 */
const formatForDisplay = (digits18: string): string => {
  if (!digits18 || digits18.length !== 18) return digits18;

  const bankCode = digits18.substring(0, 3);
  const accountNumber = digits18.substring(3, 16);
  const controlNumber = digits18.substring(16, 18);

  // Remove leading zeros from account number for display
  const trimmedAccount = accountNumber.replace(/^0+/, "") || "0";

  return `${bankCode}-${trimmedAccount}-${controlNumber}`;
};

/**
 * Sanitizes input - allows only digits and dashes
 */
const sanitizeInput = (input: string): string => {
  return input.replace(/[^\d-]/g, "");
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

function VolonterProfile({ cancel, logout }: any) {
  const [userData, setUserData] = useState<any>(null);

  useEffect(() => {
    const email = localStorage.getItem("email") || "";
    fetchUserData(email).then((res: any) => setUserData(res.data));
  }, []);

  return (
    <div className={styles.volonterProfileWrap}>
      <LeftPart logout={logout} userData={userData} setUserData={setUserData} />
      <RightPart
        cancel={cancel}
        userData={userData}
        setUserData={setUserData}
      />
    </div>
  );
}

export default VolonterProfile;

const LeftPart = ({ userData, setUserData }: any) => {
  const [userLocation, setUserLocation] = useState<any>(null);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition((position) => {
      const { latitude, longitude } = position.coords;
      axios
        .post(
          "https://maci-proxy-app-ue4um.ondigitalocean.app/cargo-api/direction/geocoder/reverse",
          {
            lat: latitude,
            lng: longitude,
            lang: "SR",
          }
        )
        .then((res) => {
          setUserLocation(res.data);
        });
    });
  }, []);

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const imageUrl = userData?.userPhoto
    ? `${process.env.REACT_APP_CDN_PATH + userData?.userPhoto}`
    : "/profile.png";
  return (
    <div className={styles.leftPart}>
      {showSuccessMessage && (
        <NotificationMessage
          className={styles.successNotification}
          onClick={() => setShowSuccessMessage(false)}
        >
          Promene uspešno sačuvane
        </NotificationMessage>
      )}
      <div className={styles.coverImage}>
        <PharmaciesImagesModal
          maxNumber={1}
          currentImages={[]}
          submitImageChange={(imageList: any) => {
            editUserImage(userData, imageList).then(() => {
              const email = localStorage.getItem("email") || "";
              fetchUserData(email).then((res: any) => setUserData(res.data));
              setShowSuccessMessage(true);
              setTimeout(() => {
                setShowSuccessMessage(false);
              }, 5000);
            });
          }}
        />
        <div className={styles.imageWrap}>
          <img
            className={styles.image}
            alt="img"
            src={imageUrl}
            width={160}
            height={160}
          />
        </div>
      </div>
      <div className={styles.volonterInfo}>
        <div className={styles.userName}>
          {userData?.firstName} {userData?.secondName}
        </div>
        <div
          style={{
            fontSize: "18px",
            fontWeight: "bold",
            color: "#fff",
            margin: "10px 0",
            textDecoration: "underline",
          }}
        >
          {userLocation?.formattedAddress}
        </div>
        <div className={styles.loyaltyPoints}>
          <HeartIcon fill="#FFF" /> {userData?.userLoyaltyPoints}
        </div>
        <Button
          onClick={() => {
            localStorage.clear();
            window.location.reload();
          }}
          style={{ width: "fit-content", margin: "20px auto" }}
          buttonType="white"
        >
          Odjavi se
        </Button>
      </div>
    </div>
  );
};

const RightPart = ({ userData, setUserData, cancel }: any) => {
  const dispatch = useDispatch();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Bank account state
  const [bankAccountInput, setBankAccountInput] = useState("");
  const [bankAccountValidation, setBankAccountValidation] =
    useState<BankAccountValidation>({ isValid: true });
  const [hasBeenTouched, setHasBeenTouched] = useState(false);

  // Signature state
  const [signature, setSignature] = useState<string | null>(null);
  const [signatureCacheKey, setSignatureCacheKey] = useState(Date.now());
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [signatureError, setSignatureError] = useState<string | null>(null);
  const signatureInputRef = useRef<HTMLInputElement>(null);

  // Fetch signature on load
  useEffect(() => {
    const fetchSignature = async () => {
      if (userData?.id) {
        try {
          const sig = await getUserSignature(userData.id);
          setSignature(sig);
        } catch (err) {
          console.error("Failed to fetch signature:", err);
        }
      }
    };
    fetchSignature();
  }, [userData?.id]);

  // Initialize bank account input when userData loads
  useEffect(() => {
    if (userData?.bankAccount) {
      // If it's 18 digits, format for display, otherwise show as-is
      const formatted =
        userData.bankAccount.length === 18
          ? formatForDisplay(userData.bankAccount)
          : userData.bankAccount;
      setBankAccountInput(formatted);
    } else {
      setBankAccountInput("");
    }
  }, [userData]);

  // Handle input changes (minimal processing)
  const handleBankAccountInputChange = (value: string) => {
    const sanitized = sanitizeInput(value);
    setBankAccountInput(sanitized);

    // Only validate if user has interacted with field
    if (hasBeenTouched) {
      const validation = validateAndNormalizeBankAccount(sanitized);
      setBankAccountValidation(validation);
    }
  };

  // Handle field blur (validate and normalize)
  const handleBankAccountBlur = () => {
    setHasBeenTouched(true);

    const validation = validateAndNormalizeBankAccount(bankAccountInput);
    setBankAccountValidation(validation);

    if (validation.isValid) {
      // Update userData with normalized value
      const normalizedValue = validation.normalizedValue || "";
      setUserData({ ...userData, bankAccount: normalizedValue });

      // Update display if we have a valid 18-digit number
      if (normalizedValue && normalizedValue.length === 18) {
        setBankAccountInput(formatForDisplay(normalizedValue));
      }
    }
  };

  // Bank account validator for TextField component
  const bankAccountValidator = () => {
    return hasBeenTouched && !bankAccountValidation.isValid;
  };

  // Signature handlers - auto-upload on file select
  const handleSignatureSelect = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file || !userData?.id) return;

    // Reset input so same file can be selected again
    if (signatureInputRef.current) {
      signatureInputRef.current.value = "";
    }

    // Validate file type
    const validTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      setSignatureError("Molimo izaberite PNG ili JPEG sliku.");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setSignatureError("Slika je prevelika. Maksimalna veličina je 5MB.");
      return;
    }

    // Immediately upload
    setSignatureLoading(true);
    setSignatureError(null);

    try {
      await uploadUserSignature(userData.id, file);
      const newSig = await getUserSignature(userData.id);
      setSignature(newSig);
      setSignatureCacheKey(Date.now());
      dispatch<any>(fetchDriverSignature(userData.id));
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 5000);
    } catch (err: any) {
      console.error("Failed to upload signature:", err);
      const errorMessage =
        err.response?.status === 500
          ? "Greška pri obradi slike. Pokušajte sa drugom slikom."
          : "Došlo je do greške. Pokušajte ponovo.";
      setSignatureError(errorMessage);
    } finally {
      setSignatureLoading(false);
    }
  };

  const handleSignatureDelete = async () => {
    if (!userData?.id) return;

    setSignatureLoading(true);
    setSignatureError(null);

    try {
      await deleteUserSignature(userData.id);
      setSignature(null);
      dispatch<any>(fetchDriverSignature(userData.id));
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 5000);
    } catch (err) {
      console.error("Failed to delete signature:", err);
      setSignatureError("Greška pri brisanju potpisa. Pokušajte ponovo.");
    } finally {
      setSignatureLoading(false);
    }
  };

  return (
    <div className={styles.rightPart}>
      {showSuccessMessage && (
        <NotificationMessage
          className={styles.successNotification}
          onClick={() => setShowSuccessMessage(false)}
        >
          Promene uspešno sačuvane
        </NotificationMessage>
      )}
      <h2>Vaš profil</h2>
      <div>
        <div style={{ display: "flex", gap: "10px" }}>
          <TextField
            value={userData?.firstName || ""}
            onChange={(val: any) => {
              setUserData({ ...userData, firstName: val });
            }}
            label="Ime"
            wrapClass={styles.textFieldWrap}
          />
          <TextField
            value={userData?.secondName || ""}
            onChange={(val: any) => {
              setUserData({ ...userData, secondName: val });
            }}
            label="Prezime"
            wrapClass={styles.textFieldWrap}
          />
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <TextField
            value={userData?.phoneNumber || ""}
            onChange={(val: any) => {
              setUserData({ ...userData, phoneNumber: val });
            }}
            label="Broj telefona"
            wrapClass={styles.textFieldWrap}
          />
          <TextField
            value={userData?.username || ""}
            onChange={(val: any) => {
              setUserData({ ...userData, username: val });
            }}
            label="Email"
            disabled
            wrapClass={styles.textFieldWrap}
          />
        </div>

        {/* Enhanced Bank Account Field */}
        <div style={{ display: "flex", gap: "10px" }}>
          <div
            className={styles.textFieldWrap}
            style={{ position: "relative" }}
          >
            <TextField
              value={bankAccountInput}
              onChange={handleBankAccountInputChange}
              onBlur={handleBankAccountBlur}
              label="Broj računa"
              placeHolder="200-123456-00 ili 200000000012345600"
              validator={bankAccountValidator}
              inputMode="numeric" // Better mobile experience
              maxLength={25} // Allow for various input formats
            />

            {/* Error message */}
            {hasBeenTouched &&
              !bankAccountValidation.isValid &&
              bankAccountValidation.errorMessage && (
                <div
                  style={{
                    color: "#ff4444",
                    fontSize: "12px",
                    marginTop: "4px",
                    lineHeight: "1.3",
                  }}
                >
                  {bankAccountValidation.errorMessage}
                </div>
              )}

            {/* Helper text */}
            <div
              style={{
                color: "#666",
                fontSize: "11px",
                marginTop:
                  hasBeenTouched && !bankAccountValidation.isValid
                    ? "2px"
                    : "4px",
                fontStyle: "italic",
                lineHeight: "1.3",
              }}
            >
              Potrebno za QR kod dostave • Format: XXX-YYYYY-ZZ ili 18 cifara
            </div>

            {/* Success indicator */}
            {hasBeenTouched &&
              bankAccountValidation.isValid &&
              bankAccountInput && (
                <div
                  style={{
                    color: "#28a745",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  ✓ Ispravan broj računa
                </div>
              )}
          </div>
        </div>

        {/* Signature Section */}
        <div className={styles.signatureSection}>
          <h3>Digitalni potpis</h3>

          {signature ? (
            <div className={styles.currentSignature}>
              <img
                src={`${process.env.REACT_APP_CDN_PATH}${signature}?t=${signatureCacheKey}`}
                alt="Trenutni potpis"
              />
              <div className={styles.signatureActions}>
                <Button
                  buttonType="blue"
                  onClick={() => signatureInputRef.current?.click()}
                  disabled={signatureLoading}
                >
                  {signatureLoading ? "Učitavanje..." : "Promeni potpis"}
                </Button>
                <Button
                  buttonType="white"
                  onClick={handleSignatureDelete}
                  disabled={signatureLoading}
                >
                  Ukloni potpis
                </Button>
              </div>
            </div>
          ) : (
            <div className={styles.signatureUpload}>
              <Button
                buttonType="blue"
                onClick={() => signatureInputRef.current?.click()}
                disabled={signatureLoading}
              >
                {signatureLoading ? "Učitavanje..." : "Dodaj potpis"}
              </Button>
            </div>
          )}

          {/* Hidden file input */}
          <input
            ref={signatureInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg"
            onChange={handleSignatureSelect}
            style={{ display: "none" }}
          />

          {/* Error message */}
          {signatureError && (
            <div className={styles.signatureError}>{signatureError}</div>
          )}
        </div>

        <div style={{ display: "flex", gap: "10px", margin: "40px 0" }}>
          <Button
            onClick={() =>
              editUserData(userData).then(() => {
                const email = localStorage.getItem("email") || "";
                fetchUserData(email).then((res: any) => setUserData(res.data));
                setShowSuccessMessage(true);
                setTimeout(() => {
                  setShowSuccessMessage(false);
                }, 5000);
              })
            }
            buttonType="blue"
          >
            Sačuvaj
          </Button>
          <Button onClick={() => cancel()} buttonType="white">
            Odustani
          </Button>
        </div>
      </div>
    </div>
  );
};

export const fetchUserData = (email: string) => {
  return axios.get(baseUrl + `/user/${email}`);
};

export const editUserData = (data: any) => {
  return axios.put(baseUrl + `/user`, data);
};

export const editUserImage = (data: any, image: any) => {
  let userDataDraft = data;
  const formData = new FormData();

  if (image.length > 0) {
    userDataDraft = {
      ...userDataDraft,
      userPhoto: image[0].file.name,
    };
    formData.append("userRequestString", JSON.stringify(userDataDraft));
    formData.append("userPhoto", image[0].file);
  }

  return axios.put(baseUrl + `/user/update_profile`, formData);
};
